import createTrackPoint from '@gaia/utils/gaia/createTrackPoint';
import {
    trackFixedLayerIds,
    waypointFixedLayerIds,
} from '@gaia/utils/gaia/getSyncMapboxStyle/fixedLayerIds';
import isEditableRoute from '@gaia/utils/gaia/isEditableRoute';
import removeTrackPoint from '@gaia/utils/gaia/removeTrackPoint';
import type {
    MapCursor,
    UseInteractionHandlersOptions,
} from '@gaia/utils/gaia/useInteractionHandlers';
import isEqual from 'lodash/isEqual';

let idleTimeout: number | undefined;

export const editingRoute = {
    getCursor: (interaction: EditingRouteInteractionState): MapCursor => {
        if (!isEditableRoute(interaction.track)) {
            return 'grab';
        } else if (interaction.subtype === 'appendingPoint') {
            return 'crosshair';
        } else if (interaction.subtype === 'hoveringPoint') {
            return 'grab';
        } else if (interaction.subtype === 'draggingPoint') {
            return 'grabbing';
        } else {
            return 'default';
        }
    },

    onMouseDown: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            onInteractionChange({...interaction, subtype: 'clickingPoint'});
        } else if (interaction.subtype === 'hoveringLine') {
            event.preventDefault();
            onInteractionChange({...interaction, subtype: 'clickingLine'});
        }
    },

    onMouseUp: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            onInteractionChange({
                ...interaction,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        } else if (interaction.subtype === 'clickingLine') {
            event.preventDefault();

            const updatedState = createTrackPoint({
                lngLat: event.lngLat,
                track: interaction.track,
            });
            if (!updatedState) return;
            const [localId, updatedTrack] = updatedState;

            onInteractionChange({
                ...interaction,
                pointLocalId: localId,
                subtype: 'clickingPoint',
                track: updatedTrack,
            });
        }
    },

    onMouseMove: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        const map = event.target;
        if (!isEditableRoute(interaction.track)) return;

        if (idleTimeout) clearTimeout(idleTimeout);
        idleTimeout = window.setTimeout(() => {
            onInteractionChange((currentInteraction) => {
                if (currentInteraction.subtype === 'appendingPoint') {
                    return {...currentInteraction, idle: true};
                } else {
                    return currentInteraction;
                }
            });
        }, 150);

        if (interaction.subtype === 'clickingPoint' || interaction.subtype === 'draggingPoint') {
            if (!interaction.track.isGeometryLoaded) return;

            onInteractionChange({
                ...interaction,
                subtype: 'draggingPoint',
                track: {
                    ...interaction.track,
                    points: {
                        ...interaction.track.points,
                        [interaction.pointLocalId]: {
                            ...interaction.track.points[interaction.pointLocalId],
                            properties: {
                                ...interaction.track.points[interaction.pointLocalId].properties,
                                isGeometryModified: true,
                            },
                            geometry: {
                                type: 'Point',
                                coordinates: event.lngLat.toArray(),
                            },
                        },
                    },
                },
            });
        } else if (interaction.subtype === 'clickingLine') {
            const {toPointLocalId} = interaction;

            const updatedState = createTrackPoint({
                lngLat: event.lngLat,
                track: interaction.track,
                insertBeforePointLocalId: toPointLocalId,
            });
            if (!updatedState) return;
            const [localId, updatedTrack] = updatedState;

            onInteractionChange({
                ...interaction,
                pointLocalId: localId,
                subtype: 'draggingPoint',
                track: updatedTrack,
            });
        } else {
            const bboxOffset = 12;

            const lineFeatures = map.queryRenderedFeatures(
                [
                    [event.point.x - bboxOffset, event.point.y + bboxOffset],
                    [event.point.x + bboxOffset, event.point.y - bboxOffset],
                ],
                {
                    layers: [trackFixedLayerIds.editing.normal].filter((id) => map.getLayer(id)),
                    filter: ['==', ['get', 'localId'], interaction.track.properties.localId],
                }
            );

            const pointFeatures = map.queryRenderedFeatures(
                [
                    [event.point.x - bboxOffset, event.point.y - bboxOffset],
                    [event.point.x + bboxOffset, event.point.y + bboxOffset],
                ],
                {
                    layers: [
                        trackFixedLayerIds.editing.point,
                        waypointFixedLayerIds.localShared.pin,
                        waypointFixedLayerIds.localUser.pin,
                    ].filter((id) => map.getLayer(id)),
                    filter: ['==', ['get', 'trackLocalId'], interaction.track.properties.localId],
                }
            );

            const [feature] = [...pointFeatures, ...lineFeatures];

            if (!feature) {
                onInteractionChange({
                    ...interaction,
                    subtype: 'appendingPoint',
                    idle: false,
                    cursorLngLat: event.lngLat,
                });
            } else if (feature.geometry.type === 'Point') {
                onInteractionChange({
                    ...interaction,
                    pointLocalId: (feature as any as TrackPoint).properties.localId,
                    subtype: 'hoveringPoint',
                    lngLat: event.lngLat,
                });
            } else {
                onInteractionChange({
                    ...interaction,
                    toPointLocalId: (feature as any as TrackLine).properties.toPointLocalId,
                    subtype: 'hoveringLine',
                    lngLat: event.lngLat,
                });
            }
        }
    },

    onMouseOut: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        if (interaction.subtype === 'appendingPoint') {
            onInteractionChange({...interaction, cursorLngLat: undefined});
        }
    },

    onClick: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
            setSelections,
            selections,
            sidebar,
            onSidebarChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        if (sidebar.type !== 'editTrack') {
            // Reopen the sidebar if it was closed while editing. This supports the use case
            // where a user might need to change layers or other properties while editing a track.
            onSidebarChange({
                type: 'editTrack',
                id: interaction.track.properties.localId,
            });
        } else if (interaction.subtype === 'appendingPoint' && isEditableRoute(interaction.track)) {
            const updatedState = createTrackPoint({
                lngLat: event.lngLat,
                track: interaction.track,
            });
            if (!updatedState) return;
            const [localId, updatedTrack] = updatedState;

            onInteractionChange({
                ...interaction,
                pointLocalId: localId,
                subtype: 'hoveringPoint',
                track: updatedTrack,
                lngLat: event.lngLat,
            });
        } else if (interaction.subtype === 'hoveringPoint') {
            const nextSelections: SelectionState[] = [
                {
                    type: 'waypoint',
                    localId: interaction.pointLocalId,
                },
            ];
            setSelections(isEqual(selections, nextSelections) ? [] : nextSelections);
        } else if (interaction.subtype === 'clickingPoint') {
            onInteractionChange({
                ...interaction,
                subtype: 'hoveringPoint',
                lngLat: event.lngLat,
            });
        }
    },

    onDblClick: (
        event: mapboxgl.MapTouchEvent | mapboxgl.MapMouseEvent,
        {
            interaction,
            onInteractionChange,
        }: UseInteractionHandlersOptions<EditingRouteInteractionState>
    ) => {
        if (interaction.subtype === 'hoveringPoint') {
            event.preventDefault();
            const {pointLocalId, track} = interaction;
            const updatedTrack = removeTrackPoint(pointLocalId, track);
            if (!updatedTrack) return;

            onInteractionChange({...interaction, track: updatedTrack});
        }
    },
};
